import { type ReactNode, useEffect, useState } from "react";

import { m } from "@vega-core/paraglide/messages";

import { Switch } from "../Switch/Switch";

type UseRowSelectionArgs<TItem> = {
  /**
   * Pass unique id for clearing selection when switching between different data sets without unmounting the table component
   */
  tableId: string;
  getRowId: (item: TItem) => string;
};

type UseRowSelectionReturn<TItem> = {
  // DataGrid wiring. `rows` is the base (filtered/paginated) set currently
  // rendered; it is a per-call parameter rather than a hook arg so the hook can
  // be created before the filter config that depends on `showSelectedOnly`.
  rowSelection: Record<string, boolean>;
  getDisplayRows: (rows: TItem[]) => TItem[];
  onSelectedRowsChange: (next: Record<string, boolean>, rows: TItem[]) => void;

  // toggle
  showSelectedOnly: boolean;
  renderShowSelectedOnlyToggle: ({
    onShow,
  }: {
    onShow?: () => void;
  }) => ReactNode;

  // selection state
  selectedItems: TItem[];
  selectedIds: string[];
  selectedCount: number;

  // mutators
  clearSelection: () => void;
  deselect: (ids: string[]) => void;
};

const toRowSelection = (
  selectedMap: Record<string, unknown>,
): Record<string, boolean> =>
  Object.fromEntries(Object.keys(selectedMap).map((id) => [id, true]));

// Selection is stored as a map of full rows (keyed by getRowId) rather than
// booleans, so rows hidden by an active filter or another page stay selected.
const buildUpdatedSelections = <TItem,>({
  newValue,
  selectedMap,
  visibleRows,
  getRowId,
}: {
  newValue: Record<string, boolean>;
  selectedMap: Record<string, TItem>;
  visibleRows: TItem[];
  getRowId: (item: TItem) => string;
}): Record<string, TItem> => {
  const visibleIds = new Set(visibleRows.map(getRowId));

  const selectionsHiddenFromView = Object.fromEntries(
    Object.entries(selectedMap).filter(([id]) => !visibleIds.has(id)),
  );

  const visibleSelections = Object.entries(newValue).reduce(
    (acc, [id, isSelected]) => {
      if (!isSelected) return acc;

      if (id in selectedMap) {
        acc[id] = selectedMap[id];

        return acc;
      }

      const row = visibleRows.find((item) => getRowId(item) === id);

      if (row) acc[id] = row;

      return acc;
    },
    {} as Record<string, TItem>,
  );

  return { ...selectionsHiddenFromView, ...visibleSelections };
};

export const useRowSelection = <TItem,>({
  tableId,
  getRowId,
}: UseRowSelectionArgs<TItem>): UseRowSelectionReturn<TItem> => {
  const [selectedMap, setSelectedMap] = useState<Record<string, TItem>>({});
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const rowSelection = toRowSelection(selectedMap);
  const selectedItems = Object.values(selectedMap);
  const selectedIds = Object.keys(selectedMap);
  const selectedCount = selectedIds.length;

  const getDisplayRows = (rows: TItem[]) =>
    showSelectedOnly ? selectedItems : rows;

  const onSelectedRowsChange = (
    next: Record<string, boolean>,
    rows: TItem[],
  ) => {
    setSelectedMap((prev) => {
      const updated = buildUpdatedSelections({
        newValue: next,
        selectedMap: prev,
        visibleRows: getDisplayRows(rows),
        getRowId,
      });

      if (Object.keys(updated).length === 0) setShowSelectedOnly(false);

      return updated;
    });
  };

  const clearSelection = () => {
    setSelectedMap({});
    setShowSelectedOnly(false);
  };

  const deselect = (ids: string[]) => {
    const idsToRemove = new Set(ids);

    setSelectedMap((prev) => {
      const remaining = Object.fromEntries(
        Object.entries(prev).filter(([id]) => !idsToRemove.has(id)),
      );

      if (Object.keys(remaining).length === 0) setShowSelectedOnly(false);

      return remaining;
    });
  };

  const renderShowSelectedOnlyToggle = ({
    onShow,
  }: {
    onShow?: () => void;
  }): ReactNode =>
    selectedCount > 0 ? (
      <Switch
        checked={showSelectedOnly}
        dataTestId="show-selected-only-toggle"
        label={m.dataGrid_showSelectedOnly()}
        labelClassName="text-sm"
        onChange={(value) => {
          setShowSelectedOnly(value);

          if (value) onShow?.();
        }}
      />
    ) : null;

  useEffect(
    () => () => {
      clearSelection();
    },
    [tableId],
  );

  return {
    rowSelection,
    getDisplayRows,
    onSelectedRowsChange,
    showSelectedOnly,
    renderShowSelectedOnlyToggle,
    selectedItems,
    selectedIds,
    selectedCount,
    clearSelection,
    deselect,
  };
};
